import React from 'react';
import { Modal, TitleCard } from '../../../components';
import { BarChartCard, type BarChartData } from './BarChartCard';
import type { AnalyticsPeriod } from '../const';
import { formatDateByScale } from '../utils/dateFormatting';
import { MetricCardType, AnalyticsMetric } from '../const';
import { getYAxisTicks } from '../utils/valueFormatting';

type BarChartModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  data: Array<{
    bucket: string;
    value: string;
    conversationNumber?: number | null;
    metricType?: string;
  }>;
  period?: AnalyticsPeriod;
  unit?: string | { minute: string; seconds: string };
  scale?: string | null;
  chartType?: AnalyticsMetric | MetricCardType;
};

export const BarChartModal: React.FC<BarChartModalProps> = ({
  isOpen,
  onClose,
  title,
  data,
  unit,
  scale,
  chartType,
}) => {
  const processedData = data.map(item => {
    const date = new Date(item.bucket);
    const displayName = formatDateByScale(date, scale || null);
    const rawValue = parseFloat(item.value) || 0;

    let formattedValue = rawValue;
    if (item.metricType) {
      switch (item.metricType) {
        case MetricCardType.DURATION_SECONDS:
          formattedValue = rawValue;
          break;
        case MetricCardType.AVERAGE_RESPONSE_TIME_SECONDS:
          formattedValue = Math.round(rawValue * 10) / 10;
          break;
        case MetricCardType.CSAT_STARS:
          formattedValue = Math.round(rawValue * 10) / 10;
          break;
        default:
          formattedValue = rawValue;
      }
    }

    return {
      name: displayName,
      value: formattedValue,
      timestamp: item.bucket,
      metricType: item.metricType,
    };
  });

  const chartData: BarChartData = {
    id: 'modal-chart',
    title,
    type: (chartType as AnalyticsMetric) || AnalyticsMetric.HUMAN_ESCALATION,
    data: processedData,
    unit: unit as string | { minute: string; seconds: string } | undefined,
    scale,
  };

  return (
    <Modal
      size="medium"
      open={isOpen}
      onClose={onClose}
      contentClassName="p-4"
      headerClassName="m-4"
      title={<TitleCard title={title} type={chartData.type} />}
    >
      <div className="p-6">
        <div className="h-[500px]">
          <BarChartCard
            item={chartData}
            height="h-full"
            showCardTooltip={false}
            hideTitle={true}
            ticks={getYAxisTicks(chartType)}
          />
        </div>
      </div>
    </Modal>
  );
};
